// =====================================================================
// typewriterRevealLogic — framework-free motion-prop resolution for the
// hero headline's width-reveal sweep (TypewriterEffectSmooth).
//
// ROOT CAUSE this exists to fix: the sweep used `whileInView` with NO
// `viewport` prop at all. Framer Motion's default viewport is
// `{ once: false }`, so any time the wrapper re-entered the viewport
// (e.g. scroll down past the hero, then scroll back up) the `width`
// tween replayed from "0%" — animating a layout property (not a
// composited transform), forcing the browser to reflow the entire
// hero title on every replay. That's the literal jitter/stutter the
// client saw on "scroll down fast then back up": the headline was
// re-collapsing and re-expanding mid-scroll.
//
// The fix is `viewport: { once: true }` so the sweep can only ever
// fire once per mount, no matter how many times the hero scrolls in
// and out of view before the sweep's own `revealed` latch flips. This
// module isolates that decision as a pure function so it's covered by
// a plain Node test (no JSX renderer in this repo — see teamCardLogic.mjs
// for the precedent) instead of only being eyeballed in TypewriterEffect.jsx.
// =====================================================================

// Returns the exact initial/whileInView/viewport/transition prop bundle
// TypewriterEffectSmooth's width-reveal wrapper should render, given:
//  - revealed: whether the sweep has already completed once (component
//    state, latched permanently true by onAnimationComplete)
//  - prefersReducedMotion: the user's OS/browser reduced-motion preference
//
// Reduced-motion and "already revealed" collapse to the same static
// result — a fixed-width, no-animation wrapper — but for different
// reasons (one is a user preference, the other is "we already played
// the sweep once"), so both are handled explicitly rather than one
// being folded silently into the other by a caller.
export function getTypewriterRevealMotion({ revealed, prefersReducedMotion }) {
  const skipSweep = Boolean(revealed) || Boolean(prefersReducedMotion)

  if (skipSweep) {
    return {
      initial: false,
      whileInView: undefined,
      // Kept even when whileInView is undefined: harmless, and keeps the
      // returned shape stable/predictable for callers and tests.
      viewport: { once: true },
      transition: { duration: 0 },
    }
  }

  return {
    initial: { width: '0%' },
    whileInView: { width: 'fit-content' },
    // The fix: without this, the sweep replays every time the hero
    // re-enters the viewport (see module doc above).
    viewport: { once: true },
    transition: { duration: 2, ease: 'linear', delay: 1 },
  }
}
